import { Cloud, CloudOff, RefreshCw } from 'lucide-react';
import { useStore } from '../store/useStore';

/** Compact sync-state indicator (sidebar / settings). */
export function SyncStatusChip() {
  const syncStatus = useStore((s) => s.syncStatus);
  const syncEmail = useStore((s) => s.syncEmail);
  const lastAccount = useStore((s) => s.lastAccount);

  if (syncStatus === 'disabled') {
    return (
      <span className="flex items-center gap-1.5 text-[11px] font-bold text-slate-500">
        <CloudOff size={13} />
        Local-only mode
      </span>
    );
  }
  if (syncStatus === 'syncing') {
    return (
      <span
        className="flex items-center gap-1.5 text-[11px] font-bold text-emerald-600 dark:text-emerald-400"
        title={syncEmail}
      >
        <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-400" />
        <Cloud size={13} />
        Sync active
      </span>
    );
  }
  if (syncStatus === 'error') {
    return (
      <span className="flex items-center gap-1.5 text-[11px] font-bold text-red-600 dark:text-red-400">
        <CloudOff size={13} />
        Sync error
      </span>
    );
  }
  // Signed out: one tap back in (13 Oct 2026). With a remembered account the
  // chip names it and signs straight in; otherwise it opens the sign-in card.
  return (
    <button
      type="button"
      onClick={() => void import('../sync/firebaseSync').then((m) => m.signInAsLastAccount())}
      className="flex max-w-full items-center gap-1.5 text-left text-[11px] font-bold text-amber-600 underline-offset-2 hover:underline dark:text-amber-400"
      title={lastAccount ? `Sign in as ${lastAccount.email}` : 'Sign in to sync'}
    >
      <RefreshCw size={13} className="shrink-0" />
      <span className="truncate">{lastAccount ? `Sign in as ${lastAccount.email}` : 'Signed out — sign in'}</span>
    </button>
  );
}

/**
 * Phones (13 Oct 2026): the sidebar chip is desktop-only, so a signed-out
 * phone had no way back except Settings. This slim bar sits under the phone
 * header — ONLY when signed out AND this device remembers an account, so
 * people who never signed in (and App Review) are never nagged.
 */
export function SignedOutBar() {
  const syncStatus = useStore((s) => s.syncStatus);
  const lastAccount = useStore((s) => s.lastAccount);
  if (syncStatus !== 'signed-out' || !lastAccount) return null;
  return (
    <button
      type="button"
      onClick={() => void import('../sync/firebaseSync').then((m) => m.signInAsLastAccount())}
      className="flex w-full items-center justify-center gap-1.5 border-b border-amber-200 bg-amber-50 px-4 py-2 text-[12px] font-bold text-amber-800 dark:border-amber-500/20 dark:bg-amber-950/30 dark:text-amber-200 lg:hidden"
    >
      <RefreshCw size={13} className="shrink-0" />
      <span className="truncate">Signed out — tap to sign in as {lastAccount.email}</span>
    </button>
  );
}
